import 'react-photo-view/dist/react-photo-view.css';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { MediaViewerToolbar } from './MediaViewerToolbar';
import { Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { abyssColors } from '@/theme/colors';
import { isImageFile } from '@/utils';
import { Attachment } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/types';
import React, { ReactElement, useMemo } from 'react';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { KPI_ATTACHMENTS_COLLECTION_NAME } from '@/modules/KpiTracker/constants';
import { StyledRemoveButton } from '@/modules/KpiTracker/components/Panel/styles';

type MediaTitleProps = {
  title?: string;
};

const MediaTitle = ({ title }: MediaTitleProps) => {
  const navbarHeight = useNavbarHeight();
  return (
    <Stack height={`${navbarHeight}px`} alignItems="center" justifyContent="center">
      <Typography color={abyssColors.neutral[200]}>{title}</Typography>
    </Stack>
  );
};

type MediaViewerProps = {
  disableDelete?: boolean;
  children: ReactElement[] | ReactElement;
  mediaFiles: Attachment[];
  handleRemoveAttachment?: (argument0: number) => void;
  handleDownloadAttachment?: (argument0: number) => void;
  collectionName?: string;
};

export const MediaViewer = ({
  disableDelete,
  children,
  mediaFiles,
  handleDownloadAttachment,
  handleRemoveAttachment,
  collectionName,
}: MediaViewerProps) => {
  const memoizedChildren = useMemo(() => {
    return React.Children.map(children, (child, index) => {
      if (!React.isValidElement(child)) {
        return null;
      }

      const mediaFile = mediaFiles.find((file) => file && file.id === child.key);
      const showRemoveIcon =
        collectionName === KPI_ATTACHMENTS_COLLECTION_NAME && handleRemoveAttachment;

      const childWithRemoveIcon = showRemoveIcon ? (
        <Stack key={child.key} position="relative" sx={{ overflow: 'visible' }}>
          {child}
          <StyledRemoveButton
            disableRipple
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleRemoveAttachment(index);
            }}
          >
            <CloseIcon style={{ fontSize: '13px' }} />
          </StyledRemoveButton>
        </Stack>
      ) : (
        <React.Fragment key={child.key}>{child}</React.Fragment>
      );

      if (!mediaFile) {
        return childWithRemoveIcon;
      }

      if (!isImageFile(mediaFile) || mediaFile.disableFullScreen) {
        return childWithRemoveIcon;
      }

      return showRemoveIcon ? (
        <Stack key={child.key} position="relative" sx={{ overflow: 'visible' }}>
          <PhotoView src={mediaFile.base64 || mediaFile.url || ''}>{child}</PhotoView>
          <StyledRemoveButton
            disableRipple
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleRemoveAttachment(index);
            }}
          >
            <CloseIcon style={{ fontSize: '13px' }} />
          </StyledRemoveButton>
        </Stack>
      ) : (
        <React.Fragment key={child.key}>
          <PhotoView src={mediaFile.base64 || mediaFile.url || ''}>{child}</PhotoView>
        </React.Fragment>
      );
    });
  }, [children, mediaFiles, collectionName, handleRemoveAttachment]);

  return (
    <PhotoProvider
      maskOpacity={0.8}
      toolbarRender={({ onScale, scale, index, onClose }) => {
        if (!mediaFiles[index] || !isImageFile(mediaFiles[index])) {
          return (
            <MediaViewerToolbar
              onClose={onClose}
              onDeleteClick={() => {
                if (handleRemoveAttachment) handleRemoveAttachment(index);
              }}
            />
          );
        }
        return (
          <MediaViewerToolbar
            disableDelete={disableDelete}
            onClose={onClose}
            onScale={onScale}
            scale={scale}
            onDownloadClick={() => {
              if (handleDownloadAttachment) handleDownloadAttachment(index);
            }}
            onDeleteClick={() => {
              if (handleRemoveAttachment) handleRemoveAttachment(index);
            }}
          />
        );
      }}
      overlayRender={({ index }) => {
        return (
          <MediaTitle
            title={
              index < mediaFiles.length && mediaFiles[index]
                ? mediaFiles[index].caption || mediaFiles[index].name
                : ''
            }
          />
        );
      }}
    >
      {memoizedChildren}
    </PhotoProvider>
  );
};
